declare const RICH_TEXT_BRAND: unique symbol;

/**
 * CMS-neutral rich-text primitive.
 *
 * A branded string carrying HTML (Sitecore JSS RichText) or a serialized
 * portable-text equivalent (Sanity). Treated as opaque by `@eneco/cms`; the
 * presentation layer is responsible for safe rendering.
 */
export type RichText = string & { readonly [RICH_TEXT_BRAND]: 'RichText' };

/**
 * Cast a plain string into the `RichText` brand. Adapters call this once
 * after they've validated and (when relevant) sanitized the markup.
 */
export const asRichText = (value: string): RichText => value as RichText;

/**
 * The rich text of a single paragraph, without its `<p>` wrapper. Sanity's Portable Text always
 * serializes to a block, while the same Sitecore Rich Text fields often hold bare text, so a field shown
 * inline (inside a line of text) would otherwise gain a paragraph of its own. Anything else, several
 * paragraphs or `undefined` included, is returned unchanged.
 *
 * A stopgap: it inspects HTML the Sanity mapper has just serialized, when the structure (one text block)
 * was known a step earlier. Once Portable Text is rendered directly (see `toRichText` in
 * `libs/cms-sanity/src/mappers.ts`), inline rendering should output that block's children instead.
 */
export const stripOuterParagraph = (html: string | undefined): string | undefined => {
  const match = /^<p>([\s\S]*)<\/p>$/.exec(html?.trim() ?? '');
  return match && !match[1].includes('<p') ? match[1] : html;
};
